import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { CheckCircle2, Info, AlertCircle, Quote, ArrowRight, Plus, X, Calendar, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

const DepressionAccordionItem = ({ title, icon: Icon, children, isOpen, onClick }) => {
  return (
    <div className="bg-white rounded-2xl border border-[#E8DDCE] overflow-hidden shadow-sm h-fit">
      <button
        onClick={onClick}
        className="flex w-full items-center justify-between p-6 text-left hover:bg-[#F4EFE7]/30 transition-colors group cursor-pointer"
      >
        <span className="text-xl font-serif text-[#123F13] flex items-center gap-3">
          <Icon className="text-[#8C2F12] w-6 h-6" /> {title}
        </span>
        <div className="text-[#3B352F]/40 transition-transform duration-300">
          {isOpen ? <X size={20} /> : <Plus size={20} />}
        </div>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="p-6 pt-0 border-t border-[#E8DDCE]/50">
              <div className="pt-4">
                {children}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Depression = () => {
  const [openItems, setOpenItems] = useState([]);
  const canonicalUrl = "https://corehypnose.dk/depression";

  const toggleItem = (value) => {
    setOpenItems(prev => 
      prev.includes(value) 
        ? prev.filter(item => item !== value)
        : [...prev, value]
    );
  };

  const treatmentSteps = [
    "Forståelse: Jeg starter med at skabe ro og klarhed over, hvad du oplever lige nu",
    "Metakognitive teknikker: Du lærer at stoppe overanalyse og grublerier",
    "Hypnose: Fører dig til ro i kroppen og øger mental balance",
    "Hjemmeopgaver: Enkle, evidensbaserede redskaber til hverdagen",
    "Fastholdelse af forandring: Strategier til at bevare velvære og undgå tilbagefald"
  ];

  return (
    <ServiceLayout 
      title="Behandling af depression – få glæde og energi tilbage i hverdagen"
      subtitle="Depression påvirker tanker, følelser og krop, men det kan behandles. Hos Core Hypnose kombinerer jeg hypnose og metakognitiv terapi, så du får ro, retning og mere energi i hverdagen."
      description="Depression er mere end midlertidig tristhed, det er en tilstand præget af vedvarende følelser af håbløshed, nedsat interesse og energimangel. Symptomer kan være søvnforstyrrelser, ændret appetit, værdiløshedsfølelse og selvmordstanker."
      image="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/d0b3d28a-a9b2-4107-9eb3-26051ca4f501.png"
      benefits={[
        "Dokumenteret effektiv behandling",
        "Mærk forandring allerede fra første session",
        "Få enkle redskaber der virker",
        "Du behøver ikke vente",
        "Find håb og retning igen"
      ]}
      content={treatmentSteps}
      showContentSection={false}
      metaTitle="Depression Behandling I KBH | Metakognitiv Terapi Og Hypnose"
      metaDescription="Effektiv behandling af depression med Metakognitiv terapi og Hypnose. Mere end 25 års erfaring. ingen ventetid. Gratis afklarende samtale."
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#193f10] text-white px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md shrink-0">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#faf7f2] transition-all shrink-0">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </>
      }
      footer={
        <div className="text-center pb-16">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Klar til at få ro og kontrol igen?</h2>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#a33a1a] transition-all shadow-lg">
            Book din første session nu <ArrowRight size={20} />
          </Link>
        </div>
      }
    >
      <Helmet>
        <title>Depression Behandling I KBH | Metakognitiv Terapi Og Hypnose</title>
        <meta name="description" content="Effektiv behandling af depression med Metakognitiv terapi og Hypnose. Mere end 25 års erfaring. ingen ventetid. Gratis afklarende samtale." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Depression Behandling I KBH | Metakognitiv Terapi Og Hypnose" />
        <meta property="og:description" content="Effektiv behandling af depression med Metakognitiv terapi og Hypnose. Mere end 25 års erfaring. ingen ventetid. Gratis afklarende samtale." />
        <meta property="og:image" content="" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="" />
      </Helmet>

      {/* Testimonial Section - Karsten */}
      <div className="mb-20 bg-[#123F13] text-white p-8 md:p-12 rounded-2xl relative overflow-hidden">
        <Quote className="absolute top-8 right-8 text-white/10" size={120} />
        <div className="relative z-10 max-w-2xl">
          <h3 className="text-2xl font-serif mb-6 italic">“Det føltes som om, jeg var trådt i beton”</h3>
          <div className="text-white/80 mb-6 leading-relaxed space-y-4">
            <p>Karsten følte sig tom og energiforladt. Hver dag var en kamp mod manglende interesse og en konstant indre uro.</p>
            <p>Efter 6 sessioner med metakognitiv terapi og hypnose oplevede han, at “det var som om, verden begyndte at komme ind igen”, og han genfandt lysten til livet.</p>
          </div>
          <p className="font-bold">— Karsten, 35 år</p>
        </div>
      </div>

      {/* Hvorfor bliver depressionen ved at vende tilbage? */}
      <div className="mb-20">
        <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvorfor bliver depressionen ved at vende tilbage?</h2>
        <div className="text-[#3B352F]/70 text-lg leading-relaxed space-y-4">
          <p>
            Depression føles ofte som noget, der bare rammer – men det, der holder den fast, er sjældent selve følelsen. Det er den måde, du forsøger at forstå, kontrollere eller analysere den på.
          </p>
          <p>
            Når du bruger meget tid på at tænke “hvorfor har jeg det sådan?”, “hvor længe varer det?” eller “hvordan slipper jeg ud af det?”, sætter sindet sig fast i en ond cirkel af grublerier, selvfokus og selvbebrejdelser. Og så bliver det hele tungere.
          </p>
          <p>
            Behandlingen hos Core Hypnose handler om at bryde den cirkel. Du lærer at skabe afstand til de tanke- og følelsesmønstre, der holder dig fast – og genopdager, at du godt kan føle dig let og levende igen.
          </p>
          <p className="text-base border-l-2 border-[#8C2F12]/30 pl-4 py-1">
            Nogle gange hænger en følelse af tristhed, tomhed eller depression også sammen med svære perioder eller fastlåste mønstre i parforholdet. Her kan <Link to="/parterapi" className="text-[#8C2F12] font-semibold hover:underline">parterapi eller relationscoaching</Link> hjælpe jer med at genfinde forbindelsen og støtte hinanden.
          </p>
        </div>
      </div>

      {/* Symptomer */}
      <div className="mb-20 max-w-5xl mx-auto">
        <h2 className="text-3xl font-serif text-[#123F13] mb-10 text-center">Typiske symptomer på depression</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
          <DepressionAccordionItem 
            title="Fysiske & Mentale tegn" 
            icon={AlertCircle}
            isOpen={openItems.includes('physical')}
            onClick={() => toggleItem('physical')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Vedvarende tristhed eller tomhed</strong></li>
              <li>• <strong>Tankemylder og selvkritik</strong></li>
              <li>• <strong>Søvnproblemer</strong> – svært ved at falde i søvn eller sover for meget</li>
              <li>• <strong>Lav energi</strong> og følelsen af, at alting kræver en indsats</li>
              <li>• <strong>Fysiske smerter</strong> uden klar årsag</li>
            </ul>
          </DepressionAccordionItem>

          <DepressionAccordionItem 
            title="Følelser & Adfærd" 
            icon={Info}
            isOpen={openItems.includes('behavior')}
            onClick={() => toggleItem('behavior')}
          >
            <ul className="space-y-4 text-[#3B352F]/70">
              <li>• <strong>Tab af interesse</strong> for ting, du plejede at nyde</li>
              <li>• <strong>Appetitændringer</strong> og vægtændring</li>
              <li>• <strong>Koncentrationsbesvær</strong> og beslutningsvanskeligheder</li>
              <li>• <strong>Følelse af skyld, skam eller meningsløshed</strong></li>
              <li>• <strong>Tanker om ikke at være noget værd</strong> – eller ikke at ville være her</li>
            </ul>
          </DepressionAccordionItem>
        </div>
        

      </div>

      {/* Hvorfor min behandling virker */}
      <div className="mb-20">
        <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvorfor min behandling virker mod depression</h2>
        <p className="text-[#3B352F]/70 mb-8 text-lg">
          Hos Core Hypnose fokuserer jeg ikke på lange analyser eller gamle traumer. Jeg arbejder med det, du mærker og tænker her og nu, og hvordan du håndterer dine symptomer mere hensigtsmæssigt.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="space-y-6">
            <div className="flex gap-4 items-start">
              <div className="bg-[#123F13] text-white p-2 rounded-lg shrink-0">
                <CheckCircle2 size={24} />
              </div>
              <div>
                <h3 className="text-xl font-serif text-[#123F13] mb-2">Metakognitiv terapi</h3>
                <p className="text-[#3B352F]/70">
                  Hjælper dig med at genkende og håndtere de vaner og overbevisninger, der fastholder dig i grublerier og selvkritik. Du lærer at skabe afstand til de tanker, der ellers styrer dit humør, og forstå, at dit sind kan regulere sig selv, hvis du lader tankerne være.
                </p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="bg-[#123F13] text-white p-2 rounded-lg shrink-0">
                <CheckCircle2 size={24} />
              </div>
              <div>
                <h3 className="text-xl font-serif text-[#123F13] mb-2">Hypnose</h3>
                <p className="text-[#3B352F]/70">
                  Skaber en dyb mental ro og styrker din evne til at regulere følelser og slippe spændinger i kroppen.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#F4EFE7] p-8 rounded-2xl border border-[#E8DDCE]">
            <p className="text-[#3B352F]/70 mb-4">
              Studier viser, at op mod <strong>80 % oplever markant bedring</strong> gennem metakognitiv terapi, ofte allerede efter få sessioner.
            </p>
            <p className="text-[#3B352F]/70 mb-4">
              Et typisk forløb varer 6–12 sessioner, og jeg tilpasser det altid til dig og din situation.
            </p>
            <p className="text-[#123F13] font-serif italic text-lg">
              “Du behøver ikke forstå alt, du skal bare mærke, at du kan få det bedre. Og det starter ofte allerede i første session.”
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Link & External Resources */}
      <div className="mb-20 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="border border-[#E8DDCE] p-8 rounded-2xl hover:bg-[#F4EFE7]/30 transition-colors">
          <h3 className="text-xl font-serif text-[#123F13] mb-4">FAQ - svar på dine spørgsmål</h3>
          <ul className="space-y-2 text-[#3B352F]/70 mb-6">
            <li>• Hvor mange sessioner har jeg brug for?</li>
            <li>• Er hypnose sikkert?</li>
            <li>• Kan jeg kombinere behandlingen med medicin?</li>
          </ul>
          <Link to="/faq" className="text-[#8C2F12] font-bold hover:underline inline-flex items-center gap-2">
            Se alle spørgsmål og svar <ArrowRight size={16} />
          </Link>
        </div>
        <div className="border border-[#E8DDCE] p-8 rounded-2xl hover:bg-[#F4EFE7]/30 transition-colors">
          <h3 className="text-xl font-serif text-[#123F13] mb-4">Vil du vide mere om depression?</h3>
          <p className="text-[#3B352F]/70 mb-6">
            Læs om symptomer, forskning og behandling på Depressionsforeningens hjemmeside.
          </p>
          <a href="https://depressionsforeningen.dk" target="_blank" rel="noopener noreferrer" className="text-[#8C2F12] font-bold hover:underline inline-flex items-center gap-2">
            Besøg Depressionsforeningen <ArrowRight size={16} />
          </a>
        </div>
      </div>

      {/* Hvordan foregår behandlingen sektion */}
      <div className="mb-20">
        <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-10">Hvordan foregår behandlingen?</h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {treatmentSteps.map((item, index) => {
            const hasColon = item.includes(':');
            const titlePart = hasColon ? item.split(':')[0].trim() : `Trin ${index + 1}`;
            const descPart = hasColon ? item.split(':').slice(1).join(':').trim() : item;
            
            // Dynamisk layout baseret på antal punkter
            const totalItems = treatmentSteps.length;
            let colSpanClasses = "";

            if (totalItems === 4) {
              colSpanClasses = "md:col-span-6 lg:col-span-3";
            } else if (totalItems === 5) {
              colSpanClasses = index >= 3 ? "md:col-span-6 lg:col-span-6" : "md:col-span-4 lg:col-span-4";
            } else if (totalItems === 3) {
              colSpanClasses = "md:col-span-4 lg:col-span-4";
            } else {
              colSpanClasses = "md:col-span-6 lg:col-span-4";
            }
            
            return (
              <div 
                key={index} 
                className={`bg-white p-8 rounded-2xl border border-[#E8DDCE] shadow-sm hover:shadow-md transition-all group relative overflow-hidden ${colSpanClasses}`}
              >
                <div className="absolute -top-4 -right-4 text-6xl font-serif text-[#123F13]/5 font-bold group-hover:text-[#8C2F12]/10 transition-colors">
                  {(index + 1).toString().padStart(2, '0')}
                </div>
                <div className="relative z-10">
                  <span className="text-[#8C2F12] font-bold text-sm mb-2 block">{(index + 1).toString().padStart(2, '0')}</span>
                  <h3 className="text-xl font-bold text-[#123F13] mb-3">{titlePart}</h3>
                  <p className="text-[#3B352F]/70 leading-relaxed">
                    {descPart}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ServiceLayout>
  );
};

export default Depression;
